import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, PlayCircle } from 'lucide-react';
import { hilfeFuerPfad, type HilfeText } from '../../lib/hilfeTexte';
import { useTutorialStore } from '../../stores/tutorialStore';
import { useEinfuehrungStore } from '../../stores/einfuehrungStore';
import { useAuthStore } from '../../stores/authStore';
import './SeitenHilfe.css';

type Props = {
  /** Nur bei ausdrücklicher Verwendung: Schlüssel fürs Merken von auf/zu. */
  id?: string;
  /** Nur bei ausdrücklicher Verwendung: der anzuzeigende Text. */
  text?: HilfeText;
};

/**
 * Der Erklärkasten oben auf jeder Seite — wie im Event Manager.
 *
 * Ohne Angaben findet er seinen Text selbst über die Adresse (und die Rolle:
 * Die Startseite erklärt der Studioleitung etwas anderes als dem Team).
 */
export function SeitenHilfe({ id, text }: Props) {
  const { pathname } = useLocation();
  const { an, geladen } = useTutorialStore();
  const tourAnfordern = useEinfuehrungStore((s) => s.tourAnfordern);
  const user = useAuthStore((s) => s.user);

  const inhalt = text ?? hilfeFuerPfad(pathname, user);
  const schluessel = id ?? (pathname.replace(/^\/+/, '').replace(/\/.*$/, '') || 'start');
  const speicher = `mail-hilfe-zu:${schluessel}`;

  const [zu, setZu] = useState<boolean>(() => {
    try {
      return localStorage.getItem(speicher) === 'ja';
    } catch {
      return false;
    }
  });

  const umschalten = () => {
    const neu = !zu;
    setZu(neu);
    try {
      localStorage.setItem(speicher, neu ? 'ja' : 'nein');
    } catch {
      // Privater Modus oder gesperrter Speicher: dann eben nur für diesen Besuch.
    }
  };

  // Erst nach geladener Einstellung zeigen, sonst blitzt der Kasten kurz auf.
  if (!geladen || !an || !inhalt) return null;

  return (
    <section className={`sh-box${zu ? ' sh-box--zu' : ''}`} data-tour="seitenhilfe" aria-label="Erklärung zu dieser Seite">
      <button
        type="button"
        className="sh-head"
        onClick={umschalten}
        aria-expanded={!zu}
        title={zu ? 'Erklärung aufklappen' : 'Erklärung zuklappen'}
      >
        <HelpCircle size={16} className="sh-icon" aria-hidden="true" />
        <span className="sh-zweck">{inhalt.zweck}</span>
        {zu ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronUp size={16} aria-hidden="true" />}
      </button>

      {!zu && (
        <div className="sh-body">
          {inhalt.abschnitte.map((a) => (
            <div className="sh-abschnitt" key={a.titel}>
              <h3 className="sh-titel">{a.titel}</h3>
              <ul className="sh-liste">
                {a.punkte.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          ))}

          {inhalt.tour && (
            <div className="sh-tour">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => tourAnfordern(inhalt.tour!)}
              >
                <PlayCircle size={14} aria-hidden="true" /> Zeig mir die Seite
              </button>
              <span className="sh-tour-text">
                Hebt die Bedienelemente der Reihe nach hervor und erklärt sie.
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
